var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState, useCallback, useMemo } from "react";
import { Button, ConfirmationModal, Loading, MultiColumnList, } from "@folio/stripes/components";
import { useCallout, useOkapiKy } from "@folio/stripes/core";
import { FormattedDate, FormattedMessage, FormattedTime, useIntl, } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchLibraryHarvestStatuses, startLibraryHarvest, startLibraryHarvestAll, } from "../../api/adminService";
import { extractServerErrorMessage } from "../../utils/errorHandler";
import { formatHarvestReason, formatHarvestTime, } from "../../utils/harvestReason";
import { LibraryReference } from "../library/LibraryReference";
import styles from "./LibraryHarvest.module.css";
var STATUS_QUERY_KEY = "admin-library-harvest-status";
var VISIBLE_COLUMNS = [
    "libraryName",
    "status",
    "startedAt",
    "recordsFetched",
    "recordsMatched",
    "recordsReceived",
    "recordsSkipped",
    "errorMessage",
    "actions",
];
var getStatusCssClass = function (status) {
    var _a;
    var classMap = {
        RUNNING: styles.statusRunning,
        QUEUED: styles.statusQueued,
        COMPLETED: styles.statusCompleted,
        FAILED: styles.statusFailed,
        NEVER_RUN: styles.statusNeverRun,
    };
    return "".concat(styles.statusBadge, " ").concat((_a = classMap[status]) !== null && _a !== void 0 ? _a : "");
};
var isActive = function (status) {
    return status.status === "RUNNING" || status.status === "QUEUED";
};
var hasCounters = function (status) {
    return status.status !== "NEVER_RUN" && status.status !== "QUEUED";
};
export var LibraryHarvest = function () {
    var _a;
    var intl = useIntl();
    var formatMessage = intl.formatMessage;
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var callout = useCallout();
    var _b = useState(false), showConfirm = _b[0], setShowConfirm = _b[1];
    var _c = useState(function () { return new Set(); }), startingLibraryIds = _c[0], setStartingLibraryIds = _c[1];
    var _d = useQuery([STATUS_QUERY_KEY], function () { return fetchLibraryHarvestStatuses(kyInstance); }, {
        refetchInterval: function (data) { return ((data === null || data === void 0 ? void 0 : data.some(isActive)) ? 5000 : false); },
    }), statuses = _d.data, isLoading = _d.isLoading;
    var hasActive = (_a = statuses === null || statuses === void 0 ? void 0 : statuses.some(isActive)) !== null && _a !== void 0 ? _a : false;
    var harvestAllMutation = useMutation(function () {
        return startLibraryHarvestAll(kyInstance);
    });
    var startOne = useMutation(function (libraryId) {
        return startLibraryHarvest(kyInstance, libraryId);
    }).mutateAsync;
    var startAll = harvestAllMutation.mutate;
    var sendErrorCallout = useCallback(function (error, fallback) { return __awaiter(void 0, void 0, void 0, function () {
        var _a, _b, _c;
        var _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    if (!(callout === null || callout === void 0)) return [3 /*break*/, 1];
                    _a = void 0;
                    return [3 /*break*/, 3];
                case 1:
                    _c = (_b = callout).sendCallout;
                    _d = {
                        type: "error"
                    };
                    return [4 /*yield*/, extractServerErrorMessage(error, formatMessage, fallback)];
                case 2:
                    _a = _c.apply(_b, [(_d.message = _e.sent(),
                            _d)]);
                    _e.label = 3;
                case 3:
                    _a;
                    return [2 /*return*/];
            }
        });
    }); }, [callout, formatMessage]);
    var runHarvestAll = useCallback(function () {
        setShowConfirm(false);
        startAll(undefined, {
            onSuccess: function (summary) {
                callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                    type: "success",
                    message: formatMessage({ id: "ui-br-legaldeposits.admin.libraryHarvest.startAll.success" }, {
                        queued: summary.queued,
                        alreadyQueued: summary.alreadyQueued,
                        unsupportedSystem: summary.unsupportedSystem,
                    }),
                });
            },
            onError: function (error) {
                return sendErrorCallout(error, formatMessage({
                    id: "ui-br-legaldeposits.admin.libraryHarvest.startAll.error",
                }));
            },
            onSettled: function () { return queryClient.invalidateQueries([STATUS_QUERY_KEY]); },
        });
    }, [startAll, callout, formatMessage, sendErrorCallout, queryClient]);
    // Per-call mutate() callbacks fire only for the latest call in React Query v3; awaiting each start keeps every library's outcome.
    var runHarvestSingle = useCallback(function (row) { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setStartingLibraryIds(function (ids) { return new Set(ids).add(row.libraryId); });
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 5, 6]);
                    return [4 /*yield*/, startOne(row.libraryId)];
                case 2:
                    _a.sent();
                    callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                        type: "success",
                        message: formatMessage({ id: "ui-br-legaldeposits.admin.libraryHarvest.start.success" }, { library: row.libraryName }),
                    });
                    return [3 /*break*/, 6];
                case 3:
                    error_1 = _a.sent();
                    return [4 /*yield*/, sendErrorCallout(error_1, formatMessage({ id: "ui-br-legaldeposits.admin.libraryHarvest.start.error" }, { library: row.libraryName }))];
                case 4:
                    _a.sent();
                    return [3 /*break*/, 6];
                case 5:
                    setStartingLibraryIds(function (ids) {
                        var next = new Set(ids);
                        next.delete(row.libraryId);
                        return next;
                    });
                    queryClient.invalidateQueries([STATUS_QUERY_KEY]);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [startOne, callout, formatMessage, sendErrorCallout, queryClient]);
    var columnMapping = useMemo(function () { return ({
        libraryName: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.library",
        }),
        status: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.status",
        }),
        startedAt: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.lastRun",
        }),
        recordsFetched: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.fetched",
        }),
        recordsMatched: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.matched",
        }),
        recordsReceived: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.received",
        }),
        recordsSkipped: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.skipped",
        }),
        errorMessage: formatMessage({
            id: "ui-br-legaldeposits.admin.libraryHarvest.column.reason",
        }),
        actions: "",
    }); }, [formatMessage]);
    var resultsFormatter = useMemo(function () { return ({
        libraryName: function (row) { return (React.createElement(LibraryReference, { name: row.libraryName, status: row.libraryStatus })); },
        status: function (row) { return (React.createElement("span", { className: getStatusCssClass(row.status) },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.status.".concat(row.status) }))); },
        startedAt: function (row) {
            return row.status === "QUEUED" ? (row.queuedAt ? (formatMessage({ id: "ui-br-legaldeposits.admin.libraryHarvest.queuedSince" }, { time: formatHarvestTime(intl, row.queuedAt) })) : ("—")) : row.startedAt ? (React.createElement(React.Fragment, null,
                React.createElement(FormattedDate, { value: new Date(row.startedAt) }),
                " ",
                React.createElement(FormattedTime, { value: new Date(row.startedAt), hour: "numeric", minute: "numeric" }))) : ("—");
        },
        recordsFetched: function (row) {
            return hasCounters(row) ? row.recordsFetched : "—";
        },
        recordsMatched: function (row) {
            return hasCounters(row) ? row.recordsMatched : "—";
        },
        recordsReceived: function (row) {
            return hasCounters(row) ? row.recordsReceived : "—";
        },
        recordsSkipped: function (row) {
            return hasCounters(row) ? row.recordsSkipped : "—";
        },
        errorMessage: function (row) {
            var reason = row.status === "FAILED" ? formatHarvestReason(intl, row) : null;
            var previous = row.previousFailure
                ? formatHarvestReason(intl, row.previousFailure)
                : null;
            if (!reason && !previous) {
                return "—";
            }
            return (React.createElement(React.Fragment, null,
                reason && React.createElement("span", { className: styles.reason }, reason),
                previous && (React.createElement("span", { className: styles.previousReason }, formatMessage({
                    id: "ui-br-legaldeposits.admin.libraryHarvest.previousFailure",
                }, { reason: previous })))));
        },
        actions: function (row) { return (React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: function () { return runHarvestSingle(row); }, disabled: row.status === "RUNNING" || startingLibraryIds.has(row.libraryId) },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.button.harvestOne" }))); },
    }); }, [runHarvestSingle, startingLibraryIds, intl, formatMessage]);
    return (React.createElement("div", { className: styles.container },
        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.title", tagName: "h2" }),
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.description" })),
        React.createElement(Button, { buttonStyle: "primary", onClick: function () { return setShowConfirm(true); }, disabled: hasActive || harvestAllMutation.isLoading }, hasActive ? (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.button.running" })) : (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.admin.libraryHarvest.button.startAll" }))),
        isLoading ? (React.createElement(Loading, null)) : (statuses &&
            statuses.length > 0 && (React.createElement("div", { className: styles.tableContainer },
            React.createElement(MultiColumnList, { contentData: statuses, visibleColumns: VISIBLE_COLUMNS, columnMapping: columnMapping, formatter: resultsFormatter, interactive: false, columnWidths: {
                    libraryName: "200px",
                    status: "140px",
                    startedAt: "160px",
                    recordsFetched: "80px",
                    recordsMatched: "80px",
                    recordsReceived: "80px",
                    recordsSkipped: "80px",
                    errorMessage: "320px",
                    actions: "120px",
                } })))),
        React.createElement(ConfirmationModal, { open: showConfirm, heading: formatMessage({
                id: "ui-br-legaldeposits.admin.libraryHarvest.confirm.heading",
            }), message: formatMessage({
                id: "ui-br-legaldeposits.admin.libraryHarvest.confirm.message",
            }), onConfirm: runHarvestAll, onCancel: function () { return setShowConfirm(false); }, confirmLabel: formatMessage({
                id: "ui-br-legaldeposits.admin.libraryHarvest.button.startAll",
            }) })));
};
